import type { ProcessEventNode } from '@bliks-io/contracts';

/**
 * The populated start event node behind an assignment/recording source, or
 * null when the source is a step recording, an unresolved id, or the legacy
 * `'START'` marker. Start anchors carry the start event node's id since
 * issue 11 (ADR 0003 P5); the backend populates them to the event display
 * shape (`nodeKind: 'event'`) on read.
 */
export function asEventNodeSource(source: unknown): ProcessEventNode | null {
  return isEventNodeSource(source) ? source : null;
}

/**
 * Type-predicate twin of {@link asEventNodeSource} — use it where the
 * surrounding union must narrow (e.g. to keep reading step-recording fields
 * after the event arm is handled).
 */
export function isEventNodeSource(source: unknown): source is ProcessEventNode {
  if (typeof source !== 'object' || source === null) return false;
  return (source as { nodeKind?: unknown }).nodeKind === 'event';
}

/**
 * Start-anchored in either era: a populated start event node (post-flip
 * rows) or the legacy `'START'` marker (in-flight rows, drained by migration
 * M2 — this arm survives one release past P6).
 */
export function isStartAnchoredSource(source: unknown): boolean {
  return source === 'START' || asEventNodeSource(source) !== null;
}
